import {
  type ClipboardEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { Input } from "./Input";

const CODE_LENGTH = 6;
const OTP_SLOT_IDS = ["d0", "d1", "d2", "d3", "d4", "d5"] as const;

interface OTPInputProps {
  onChange?: (code: string) => void;
  onComplete?: (code: string) => void;
  status?: "success" | "error" | "neutral";
  disabled?: boolean;
  autoFocus?: boolean;
}

export const OTPInput = ({
  onChange,
  onComplete,
  status = "neutral",
  disabled = false,
  autoFocus = true,
}: OTPInputProps) => {
  const [values, setValues] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) {
      refs.current[0]?.focus();
    }
  }, [autoFocus]);

  const update = (next: string[]) => {
    setValues(next);
    const code = next.join("");
    onChange?.(code);
    if (next.every((v) => v !== "")) {
      onComplete?.(code);
    }
  };

  const handleChange = (i: number, raw: string) => {
    if (raw.length > 1) {
      const digits = raw.replace(/\D/g, "").slice(0, CODE_LENGTH);
      const next = Array(CODE_LENGTH)
        .fill("")
        .map((_, j) => digits[j] || "");
      update(next);
      refs.current[Math.min(digits.length, CODE_LENGTH - 1)]?.focus();
      return;
    }

    const digit = raw.replace(/\D/g, "").slice(-1);
    if (raw && !digit) {
      return;
    }

    const next = [...values];
    next[i] = digit;
    update(next);

    if (digit && i < CODE_LENGTH - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !values[i] && i > 0) {
      const next = [...values];
      next[i - 1] = "";
      update(next);
      refs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    const digits = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (digits.length === 0) {
      return;
    }

    e.preventDefault();
    const next = Array(CODE_LENGTH)
      .fill("")
      .map((_, j) => digits[j] || "");
    update(next);
    refs.current[Math.min(digits.length, CODE_LENGTH - 1)]?.focus();
  };

  return (
    <div className="flex justify-center gap-2">
      {OTP_SLOT_IDS.map((slotId, i) => (
        <Input
          key={slotId}
          className="h-14 w-14 text-center text-xl"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          value={values[i]}
          status={status}
          disabled={disabled}
          ref={(ref) => {
            refs.current[i] = ref;
          }}
          onFocus={(e) => e.target.select()}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
};
